import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { BlogTopic_API } from "./environment";
import { useAuth } from "../context/AuthContext";
import { FaEdit, FaTrash, FaPlus, FaFilter } from "react-icons/fa";
import Swal from "sweetalert2";
import Pagination from "../pages/Pagination";

// Component for managing blog topics with add, edit, delete, filter, and multiple delete functionality
export default function BlogTopics() {
  const { userData } = useAuth();
  const [topics, setTopics] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [filters, setFilters] = useState({
    topic_id: [],
    topic_name: [],
    topic_type: [],
  });
  const [showFilterPopup, setShowFilterPopup] = useState(null);
  const [tempSelected, setTempSelected] = useState([]);
  const [searchInput, setSearchInput] = useState("");

  const columnDisplay = {
    topic_id: "Topic ID",
    topic_name: "Topic Name",
    topic_type: "Topic Type",
  };

  // Get distinct values for a column
  const getDistinctValues = (column) => {
    const values = topics.map((item) => item[column]);
    const unique = [...new Set(values)];
    return unique.sort((a, b) =>
      column === "topic_id" ? a - b : a?.toString().localeCompare(b?.toString())
    );
  };

  // Apply filters to an item
  const applyFilters = (item) => {
    return (
      (filters.topic_id.length === 0 ||
        filters.topic_id.includes(item.topic_id)) &&
      (filters.topic_name.length === 0 ||
        filters.topic_name.includes(item.topic_name)) &&
      (filters.topic_type.length === 0 ||
        filters.topic_type.includes(item.topic_type))
    );
  };

  // Filter topics
  const filteredTopics = topics.filter(applyFilters);

  const totalPages = Math.ceil(filteredTopics.length / itemsPerPage);
  const paginatedTopics = filteredTopics.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const hasActiveFilters = Object.values(filters).some((arr) => arr.length > 0);

  // Fetch all topics on component mount
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const response = await fetch(`${BlogTopic_API}/getAll`, {
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch topics");
        }
        const data = await response.json();
        setTopics(data);
      } catch (error) {
        console.error("Error fetching topics:", error);
        Swal.fire(
          "Error",
          "Failed to fetch topics. Please try again.",
          "error"
        );
      }
    };
    fetchTopics();
  }, [userData.access_token]);

  // Toggle row selection
  const toggleRowSelection = (id) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((rowId) => rowId !== id)
        : [...prevSelected, id]
    );
  };

  // Select all filtered topics
  const handleSelectAll = () => {
    if (selectedRows.length === filteredTopics.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredTopics.map((item) => item.topic_id));
    }
  };

  // Handle filter click for a column
  const handleFilterClick = (column) => {
    setShowFilterPopup(column);
    setTempSelected(filters[column] || []);
    setSearchInput("");
  };

  // Apply filter selections
  const applyFilter = () => {
    const distinct = getDistinctValues(showFilterPopup);
    const newSelected =
      tempSelected.length === distinct.length ? [] : tempSelected;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [showFilterPopup]: newSelected,
    }));
    setCurrentPage(1);
    setShowFilterPopup(null);
  };

  // Delete multiple selected rows
  const deleteSelectedRows = async () => {
    if (selectedRows.length === 0) return;

    Swal.fire({
      text: `Do you want to delete ${selectedRows.length} selected topics?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await Promise.all(
            selectedRows.map(async (id) => {
              const response = await fetch(`${BlogTopic_API}/delete/${id}`, {
                method: "DELETE",
                headers: {
                  Authorization: `Bearer ${userData.access_token}`,
                  Accept: "application/json",
                },
              });

              if (!response.ok) {
                const errorText = await response.text();
                console.error("Server Error:", errorText);
                throw new Error(`Failed to delete topic ${id}`);
              }
            })
          );

          const updatedTopics = topics.filter(
            (topic) => !selectedRows.includes(topic.topic_id)
          );
          setTopics(updatedTopics);
          setSelectedRows([]);

          Swal.fire(
            "Success!",
            "Selected topics deleted successfully",
            "success"
          );
        } catch (error) {
          console.error("Error deleting topics:", error);
          Swal.fire(
            "Error",
            "Failed to delete some topics. Please try again.",
            "error"
          );
        }
      }
    });
  };

  // Function to add a new topic using SweetAlert for input
  const addTopic = async () => {
    const { value: formValues } = await Swal.fire({
      title: "Create new topic",
      html:
        '<input id="swal-input1" class="swal2-input" placeholder="Topic Name">' +
        '<select id="swal-input2" class="swal2-select">' +
        '<option value="" disabled selected>Select Topic Type</option>' +
        '<option value="Trending">Trending</option>' +
        '<option value="Industry-related">Industry-related</option>' +
        "</select>",
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Create",
      preConfirm: () => {
        const topicName = document.getElementById("swal-input1").value;
        const topicType = document.getElementById("swal-input2").value;
        if (!topicName || !topicType) {
          Swal.showValidationMessage("Both topic name and type are required!");
          return false;
        }
        return { topic_name: topicName, topic_type: topicType };
      },
    });

    if (formValues) {
      try {
        const response = await fetch(`${BlogTopic_API}/create`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
          body: JSON.stringify(formValues),
        });

        if (!response.ok) {
          throw new Error("Failed to add topic");
        }

        const newTopic = await response.json();
        setTopics([...topics, newTopic]);
        Swal.fire("Success!", "Topic added successfully.", "success");
      } catch (error) {
        console.error("Error adding topic:", error);
        Swal.fire("Error", "Failed to add topic. Please try again.", "error");
      }
    }
  };

  // Function to edit an existing topic using SweetAlert for input
  const editTopic = async (topic) => {
    const { value: formValues } = await Swal.fire({
      title: "Edit topic",
      html:
        `<input id="swal-input1" class="swal2-input" value="${topic.topic_name}" placeholder="Topic Name">` +
        `<select id="swal-input2" class="swal2-select">` +
        `<option value="" disabled>Select Topic Type</option>` +
        `<option value="Trending" ${
          topic.topic_type === "Trending" ? "selected" : ""
        }>Trending</option>` +
        `<option value="Industry-related" ${
          topic.topic_type === "Industry-related" ? "selected" : ""
        }>Industry-related</option>` +
        `</select>`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Update",
      preConfirm: () => {
        const topicName = document.getElementById("swal-input1").value;
        const topicType = document.getElementById("swal-input2").value;
        if (!topicName || !topicType) {
          Swal.showValidationMessage("Both topic name and type are required!");
          return false;
        }
        return { topic_name: topicName, topic_type: topicType };
      },
    });

    if (formValues) {
      try {
        const response = await fetch(
          `${BlogTopic_API}/update/${topic.topic_id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userData.access_token}`,
              Accept: "application/json",
            },
            body: JSON.stringify(formValues),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to update topic");
        }

        const updatedTopic = await response.json();
        setTopics(
          topics.map((t) => (t.topic_id === topic.topic_id ? updatedTopic : t))
        );
        Swal.fire("Success!", "Topic updated successfully.", "success");
      } catch (error) {
        console.error("Error updating topic:", error);
        Swal.fire(
          "Error",
          "Failed to update topic. Please try again.",
          "error"
        );
      }
    }
  };

  // Function to delete a single topic with confirmation
  const deleteTopic = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${BlogTopic_API}/delete/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to delete topic");
        }

        setTopics(topics.filter((topic) => topic.topic_id !== id));
        setSelectedRows(selectedRows.filter((rowId) => rowId !== id));
        Swal.fire("Deleted!", "Topic has been deleted.", "success");
      } catch (error) {
        console.error("Error deleting topic:", error);
        Swal.fire(
          "Error",
          "Failed to delete topic. Please try again.",
          "error"
        );
      }
    }
  };

  // Render filter popup for a column
  const renderFilterPopup = (column) => {
    const displayName = columnDisplay[column];
    const distinctValues = getDistinctValues(column);
    const filteredValues = distinctValues.filter(
      (val) =>
        val != null &&
        val.toString().toLowerCase().includes(searchInput.toLowerCase())
    );
    const allVisibleSelected = filteredValues.every((val) =>
      tempSelected.includes(val)
    );

    const handleToggleVisible = (e) => {
      if (e.target.checked) {
        setTempSelected([...new Set([...tempSelected, ...filteredValues])]);
      } else {
        setTempSelected(
          tempSelected.filter((val) => !filteredValues.includes(val))
        );
      }
    };

    const clearSelections = () => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        [column]: [],
      }));
      setTempSelected([]);
      setShowFilterPopup(null);
    };

    return (
      <div
        className="filter-popup"
        style={{
          position: "absolute",
          top: "100%",
          left: "0",
          zIndex: 100,
        }}
      >
        <div className="filter-header">
          <span className="column-name">{displayName}</span>
          <span className="clear-filter" onClick={clearSelections}>
            Clear
          </span>
        </div>
        <div className="filter-search-container">
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onChange={handleToggleVisible}
          />
          <input
            className="filter-search"
            type="text"
            placeholder="Search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <span className="clear-search" onClick={() => setSearchInput("")}>
              ×
            </span>
          )}
        </div>
        <div className="suggestions-list">
          {filteredValues.length === 0 ? (
            <div>No results found</div>
          ) : (
            filteredValues.map((val) => (
              <label key={val?.toString() || "null"}>
                <input
                  type="checkbox"
                  checked={tempSelected.includes(val)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setTempSelected([...tempSelected, val]);
                    } else {
                      setTempSelected(tempSelected.filter((v) => v !== val));
                    }
                  }}
                />
                {val != null ? val.toString() : "N/A"}
              </label>
            ))
          )}
        </div>
        <div className="filter-buttons">
          <button onClick={() => setShowFilterPopup(null)}>Cancel</button>
          <button onClick={applyFilter}>Apply</button>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      <Helmet>
        <title>Blog Topics - SMTS Admin Panel</title>
      </Helmet>
      <div className="page-title">
        <h2>Blog Topics</h2>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th className="hide-column">
                <input
                  type="checkbox"
                  checked={
                    selectedRows.length === filteredTopics.length &&
                    filteredTopics.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </th>
              <th style={{ position: "relative" }}>
                {hasActiveFilters && (
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === filteredTopics.length &&
                      filteredTopics.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                )}
                Topic ID
                <FaFilter
                  className={`filter-icon ${
                    filters.topic_id.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("topic_id")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "topic_id" &&
                  renderFilterPopup("topic_id")}
              </th>
              <th style={{ position: "relative" }}>
                Topic Name
                <FaFilter
                  className={`filter-icon ${
                    filters.topic_name.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("topic_name")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "topic_name" &&
                  renderFilterPopup("topic_name")}
              </th>
              <th style={{ position: "relative" }}>
                Topic Type
                <FaFilter
                  className={`filter-icon ${
                    filters.topic_type.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("topic_type")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "topic_type" &&
                  renderFilterPopup("topic_type")}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedTopics.map((topic) => (
              <tr
                key={topic.topic_id}
                className={
                  selectedRows.includes(topic.topic_id) ? "selected" : ""
                }
                onDoubleClick={() => toggleRowSelection(topic.topic_id)}
              >
                <td className="hide-column">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(topic.topic_id)}
                    onChange={() => toggleRowSelection(topic.topic_id)}
                  />
                </td>
                <td>{topic.topic_id}</td>
                <td>{topic.topic_name}</td>
                <td>{topic.topic_type}</td>
                <td className="actions">
                  <button
                    className={`action-button ${
                      selectedRows.length > 1 ? "disabled-icon" : ""
                    }`}
                    onClick={() => editTopic(topic)}
                    disabled={selectedRows.length > 1}
                  >
                    <FaEdit />
                  </button>
                  <button
                    className={`action-button ${
                      selectedRows.length > 1 ? "disabled-icon" : ""
                    }`}
                    onClick={() => deleteTopic(topic.topic_id)}
                    disabled={selectedRows.length > 1}
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        setCurrentPage={setCurrentPage}
        setItemsPerPage={(value) => {
          setCurrentPage(1);
          setItemsPerPage(value);
        }}
      />

      {selectedRows.length > 0 && (
        <button className="delete-selected" onClick={deleteSelectedRows}>
          <FaTrash className="icon" title="Delete Selected" /> (
          {selectedRows.length})
        </button>
      )}

      <button className="add-button" onClick={addTopic}>
        <FaPlus />
      </button>
    </div>
  );
}
